import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import {
  ChevronDown, Building2, RefreshCw, AlertCircle, Check, Filter,
  CalendarDays, Settings2, BarChart2, FileText, CheckCircle2,
  SlidersHorizontal, Pencil, RotateCcw, Plus, LayoutGrid,
} from "lucide-react";
import { getAPI } from "../../api/frameworksApi";
import { API_URL } from "../../constants/index";
import type {
  ApiResponse,
  GrcSummaryResponse,
  AssessmentPipelineResponse,
  TopRiskApplicationsResponse,
  FindingsOverviewResponse,
} from "../types/grcOverviewDashboard";
import {
  Card, SectionTitle, KpiCard,
  PipelineWidget, DonutPanel, NoData, PoamChart, ConfigureWidgetsMessage,
} from "./GRCDashboardNew/grcDashboardHelpers";
import { DataTable, type Column } from "../components/DataTable";
import { ConfigurableCardGrid } from "../components/ConfigurableCardGrid";
import { AddCardsPanel } from "./LandingDashboard/AddCardsPanel";
import { ExtraCardsPanel } from "./LandingDashboard/ExtraCardsPanel";
import { CardLibraryCarousel } from "./LandingDashboard/CardLibraryCarousel";
import { loadLayout, saveLayout } from "./LandingDashboard/landingStore";
import {
  GRC_OV_CARDS, GRC_OV_CATEGORIES,
  GRC_OV_LAYOUT_KEY, GRC_OV_CARD_MAP,
} from "./GRCDashboardNew/grcOrgCards";
import {
  fetchMyWidgets,
  fetchRoleWidgets,
  fetchWidgetCatalog,
  widgetsToCards,
  enabledCardIds,
  roleDashboardTitle,
  roleDashboardSubtitle,
  ORG_WIDGET_TO_CARD,
  ORG_EXTRA_CARDS,
  type MyWidgetsResponse,
  type WidgetKey,
} from "./GRCDashboardNew/grcWidgets";
import { useAuthStore } from "../../stores/authStore";
import { CustomizeWidgetsPanel } from "./GRCDashboardNew/CustomizeWidgetsPanel";
import {
  KPI_DATA, FRAMEWORKS,
} from "./GRCDashboardNew/grcDashboardData";
import type {
  KpiItem, PipelineStageItem, FrameworkItem, RiskAppItem, PoamStatusItem,
} from "./GRCDashboardNew/grcDashboardData";

/* ── Static "All" sentinel — always first in the list ──────────────────── */
const ALL_ORGS_OPTION = { id: "", name: "All Organisations" };

interface OrgOption { id: string; name: string; }

/* ── Pipeline stage UI config (label → colors/icon) ────────────────────── */
const STAGE_UI: Record<string, { color: string; bgColor: string; dotColor: string; icon: React.ElementType }> = {
  "Draft":       { color: "#8b5cf6", bgColor: "#1e1b3a", dotColor: "#8b5cf6", icon: FileText    },
  "Planned":     { color: "#4f8ef7", bgColor: "#162040", dotColor: "#4f8ef7", icon: CalendarDays },
  "Approved":    { color: "#4f8ef7", bgColor: "#162040", dotColor: "#4f8ef7", icon: CheckCircle2 },
  "In Progress": { color: "#f97316", bgColor: "#2a1500", dotColor: "#f97316", icon: Settings2   },
  "Review":      { color: "#22d3ee", bgColor: "#082830", dotColor: "#22d3ee", icon: BarChart2   },
  "Completed":   { color: "#34d399", bgColor: "#082818", dotColor: "#34d399", icon: CheckCircle2 },
  "Overdue":     { color: "#ef4444", bgColor: "#280a0a", dotColor: "#ef4444", icon: AlertCircle },
};

/* ── Findings & POA&M stage → bar colour (presentation only) ───────────── */
const FINDINGS_STAGE_COLOR: Record<string, string> = {
  "Identified Finding":      "#ef4444",
  "Remediation In Progress": "#f97316",
  "Remediation On Hold":     "#f59e0b",
  "Validation And Testing":  "#eab308",
  "Completed":               "#22c55e",
  "Closed Remediated":       "#10b981",
  "Accepted Risk":           "#a78bfa",
};
/* fallback palette for any stage label the map doesn't cover */
const FINDINGS_FALLBACK_COLORS = ["#4f8ef7", "#22d3ee", "#a78bfa", "#34d399", "#f59e0b"];

/* Mock Findings & POA&M stages — used when the API returns no data so the
   card shows a populated remediation funnel instead of "No Data Available". */
const MOCK_POAM_STAGES: { stage: string; count: number }[] = [
  { stage: "Identified Finding",      count: 205 },
  { stage: "Remediation In Progress", count: 38 },
  { stage: "Remediation On Hold",     count: 14 },
  { stage: "Validation And Testing",  count: 27 },
  { stage: "Completed",               count: 52 },
  { stage: "Closed Remediated",       count: 41 },
  { stage: "Accepted Risk",           count: 9 },
];

/* ── complianceScore → severity label ──────────────────────────────────── */
function scoreToSeverity(score: number): string {
  if (score < 40) return "critical";
  if (score < 60) return "high";
  if (score < 80) return "medium";
  return "low";
}

/* ── complianceScore → bar/text colour (hex, theme-agnostic) ───────────── */
function appScoreHex(score: number): string {
  if (score >= 80) return "#10b981"; // good
  if (score >= 60) return "#f59e0b"; // moderate
  if (score >= 40) return "#f97316"; // at risk
  return "#ef4444";                  // critical
}

/* ── Top Risk Applications grid columns ────────────────────────────────── */
const riskAppColumns: Column<RiskAppItem>[] = [
  {
    key: "name",
    header: "Application",
    align: "left",
    headerAlign: "center",
    sortable: true,
    render: (r) => <span className="text-sm font-medium text-foreground/90">{r.name}</span>,
  },
  {
    key: "findings",
    header: "Open Findings",
    sortable: true,
    render: (r) => (
      <span className="text-sm font-bold tabular-nums text-foreground/90">
        {r.findings}
      </span>
    ),
  },
  {
    key: "complianceScore",
    header: "Compliance Score",
    sortable: true,
    render: (r) => {
      const c = appScoreHex(r.complianceScore);
      return (
        <div className="mx-auto flex w-24 flex-col items-end gap-1.5">
          <span className="text-sm font-bold tabular-nums" style={{ color: c }}>
            {r.complianceScore}%
          </span>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${r.complianceScore}%`, background: c }}
            />
          </div>
        </div>
      );
    },
  },
];

/* Parse a cell value to a number for sorting (handles 14, "14", "14%").
   Returns null when the value isn't numeric so callers fall back to text sort. */
function toSortNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isNaN(v) ? null : v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/* ── Build query string with optional org_id ───────────────────────────── */
function withOrg(base: string, orgId: string, extraParams = ""): string {
  const sep = base.includes("?") ? "&" : "?";
  const parts: string[] = [];
  if (orgId)       parts.push(`org_id=${orgId}`);
  if (extraParams) parts.push(extraParams);
  return parts.length ? `${base}${sep}${parts.join("&")}` : base;
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — per-org GRC dashboard (also rendered in the drill-down
   side panel). Cards are configurable: Edit layout to drag-reorder, Add
   cards to add/remove, Reset to restore the default arrangement.
   ══════════════════════════════════════════════════════════════════════ */
export function GRCDashboardNew({ embeddedOrg }: { embeddedOrg?: OrgOption } = {}) {
  /* Header title + subtitle reflect the logged-in user's role (from the API),
     same as the group dashboard — e.g. "GRC Dashboard", "ISSO Dashboard". */
  const userRoles = useAuthStore((s) => s.user?.roles) ?? [];
  const dashboardTitle = roleDashboardTitle(userRoles);
  const dashboardSubtitle = roleDashboardSubtitle(userRoles);
  const navigate = useNavigate();

  const [mounted, setMounted]         = useState(false);
  const [orgOpen, setOrgOpen]         = useState(false);
  /* multi-select org filter — applied ids drive the fetch, draft ids are the
     in-dropdown checkbox state until "Apply" is clicked. [] = all orgs. */
  const [appliedOrgIds, setAppliedOrgIds] = useState<string[]>(embeddedOrg ? [embeddedOrg.id] : []);
  const [draftOrgIds, setDraftOrgIds]     = useState<string[]>(embeddedOrg ? [embeddedOrg.id] : []);
  const [orgList, setOrgList]         = useState<OrgOption[]>([ALL_ORGS_OPTION]);
  const [orgsLoading, setOrgsLoading] = useState(true);

  /* ── API state (KPI seeds blanked → values come from the API only) ───── */
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [kpiData, setKpiData]   = useState<KpiItem[]>(() =>
    (KPI_DATA as KpiItem[]).map((k) => ({ ...k, value: 0 })),
  );
  const [pipeline, setPipeline] = useState<PipelineStageItem[]>([]);   // API-only
  /* Compliance by Framework stays on mock data (no endpoint for it yet) */
  const [frameworks] = useState<FrameworkItem[]>(FRAMEWORKS);
  /* Donut centre shows the live Active SSP count (slices stay on mock) */
  const [activeSspCount, setActiveSspCount] = useState<number>(0);
  const [riskApps, setRiskApps] = useState<RiskAppItem[]>([]);
  /* Top Risk Applications sorting (client-side, controlled) */
  const [riskSortKey, setRiskSortKey] = useState<string | null>(null);
  const [riskSortDir, setRiskSortDir] = useState<"asc" | "desc">("asc");
  const handleRiskSort = (key: string) => {
    if (riskSortKey === key) {
      setRiskSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setRiskSortKey(key);
      setRiskSortDir("asc");
    }
  };
  const sortedRiskApps = riskSortKey
    ? [...riskApps].sort((a, b) => {
        const av = a[riskSortKey as keyof RiskAppItem];
        const bv = b[riskSortKey as keyof RiskAppItem];
        /* Compliance Score (and any numeric/percentage column) sorts by value,
           not by text — coerce "14%"/"14"/14 all to the number 14. */
        const an = toSortNumber(av);
        const bn = toSortNumber(bv);
        let cmp: number;
        if (an !== null && bn !== null) cmp = an - bn;
        else cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true });
        return riskSortDir === "asc" ? cmp : -cmp;
      })
    : riskApps;
  /* Findings & POA&M status (API-only → empty shows "No Data Available") */
  const [poamStatus, setPoamStatus] = useState<PoamStatusItem[]>([]);

  /* ── Fetch all 4 endpoints in parallel ───────────────────────────────── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const orgId = appliedOrgIds.join(","); // repeatable org_id → "a,b" (empty = all)

    try {
      const [summaryRes, pipelineRes, riskRes, findingsRes] = await Promise.all([
        getAPI(withOrg(API_URL.GRC_OV_SUMMARY, orgId))              as Promise<ApiResponse<GrcSummaryResponse>>,
        getAPI(withOrg(API_URL.GRC_OV_ASSESSMENT_PIPELINE, orgId))  as Promise<ApiResponse<AssessmentPipelineResponse>>,
        getAPI(withOrg(API_URL.GRC_OV_TOP_RISK_APPS, orgId, "limit=5")) as Promise<ApiResponse<TopRiskApplicationsResponse>>,
        getAPI(withOrg(API_URL.GRC_OV_FINDINGS_OVERVIEW, orgId))    as Promise<ApiResponse<FindingsOverviewResponse>>,
      ]);

      /* 1 ── Summary → KPI cards */
      if (summaryRes?.data) {
        const s = summaryRes.data;
        setActiveSspCount(s.activePublishedSsps ?? 0);
        setKpiData((prev) => prev.map((k) => {
          if (k.id === "total_apps")       return { ...k, value: s.totalApplications };
          if (k.id === "active_ssps")      return { ...k, value: s.activePublishedSsps };
          if (k.id === "compliance_score") return { ...k, value: `${Math.round(s.overallComplianceScore ?? 0)}%` };
          if (k.id === "open_findings")    return { ...k, value: s.openFindingsCount };
          return k;
        }));
      }

      /* 2 ── Assessment pipeline (API-only → empty shows "No Data Available") */
      const rawStages = pipelineRes?.data?.stages ?? [];
      const mappedStages: PipelineStageItem[] = rawStages.map((s) => {
        const ui = STAGE_UI[s.stage] ?? STAGE_UI["Planned"];
        return {
          id:       s.stage.toLowerCase().replace(/\s+/g, "_"),
          label:    s.stage,
          count:    s.count,
          pct:      Math.round(s.percent),
          color:    ui.color,
          bgColor:  ui.bgColor,
          dotColor: ui.dotColor,
          icon:     ui.icon,
        };
      });
      setPipeline(mappedStages);
      setKpiData((prev) => prev.map((k) =>
        k.id === "assessments_running"
          ? { ...k, value: pipelineRes?.data?.totalAssessments ?? 0 }
          : k,
      ));

      /* 3 ── Top risk applications (API-only → empty shows "No Data Available") */
      const rawRisk = riskRes?.data?.rows ?? [];
      setRiskApps(rawRisk.map((r) => ({
        name:            r.applicationName ?? r.sspId,
        findings:        r.openFindingsCount,
        severity:        scoreToSeverity(r.complianceScore),
        complianceScore: Math.round(r.complianceScore ?? 0),
      })));

      /* 4 ── Findings & POA&M overview (falls back to mock stages when empty) */
      const apiFindings = findingsRes?.data?.stages ?? [];
      const rawFindings = apiFindings.length ? apiFindings : MOCK_POAM_STAGES;
      setPoamStatus(rawFindings.map((s, i) => ({
        label: s.stage,
        count: s.count,
        color: FINDINGS_STAGE_COLOR[s.stage]
          ?? FINDINGS_FALLBACK_COLORS[i % FINDINGS_FALLBACK_COLORS.length],
      })));

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "";
      if (msg !== "OFFLINE") {
        setError("Could not load live data. Showing cached values.");
      }
    } finally {
      setLoading(false);
    }
  }, [appliedOrgIds]);

  useEffect(() => { setMounted(true); }, []);

  /* ── Load org list once on mount ─────────────────────────────────────── */
  useEffect(() => {
    if (embeddedOrg) { setOrgsLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await getAPI(`${API_URL.ORG_UNITS}?flat=true`);
        if (cancelled) return;
        const raw: Array<{ id: string; name: string; orgType?: string }> = res?.data ?? [];
        const orgs = raw
          .filter((o) => o.orgType === "ORGANIZATION")
          .map((o) => ({ id: o.id, name: o.name }));
        setOrgList([ALL_ORGS_OPTION, ...orgs]);
      } catch {
        /* silently keep the "All Organisations" fallback */
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { if (mounted) fetchAll(); }, [mounted, fetchAll]);

  /* org multi-select handlers */
  const toggleDraftOrg = (id: string) => {
    if (id === "") { setDraftOrgIds([]); return; } // "All Organisations" → clear
    setDraftOrgIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const applyOrgFilter = () => {
    setAppliedOrgIds(draftOrgIds);
    setOrgOpen(false);
  };

  const [widgetsResponse, setWidgetsResponse] = useState<MyWidgetsResponse | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  /* ── Configurable layout: add / remove / drag-reorder cards ──────────── */
  const [layout, setLayout] = useState<string[]>(() => loadLayout(GRC_OV_LAYOUT_KEY, []));
  const [editing, setEditing] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  /* In-page "Extra cards" catalog (selected on top, available below). */
  const [extraOpen, setExtraOpen] = useState(false);
  /* When opening the side panel from a "View detail" tile, expand that card. */
  const [panelCardId, setPanelCardId] = useState<string | undefined>(undefined);
  /* Available cards (side panel / "More cards") = ONLY the role's enabled widgets
     from /my-widgets (the Dashboard Config) plus the always-available extras — not
     the full catalog. The cards shown directly come from the same enabled set, so
     saving in Dashboard Config appears here immediately. */
  const [availableIds, setAvailableIds] = useState<string[]>([]);
  /* True when the role has no enabled widgets → render the configure message. */
  const [widgetsDisabled, setWidgetsDisabled] = useState(false);
  const defaultIdsRef = useRef<string[]>([]);
  /* Admin-chosen card ids for the user's role(s) — from the role-widgets API
     (GET /dashboard-widgets/orgs/{orgId}/roles/{roleId}). Drives the "Add cards"
     universe so a card the user turns OFF still appears there (as Off). */
  const adminUniverseRef = useRef<string[]>([]);
  const hadSavedLayout = useRef<boolean>(
    typeof window !== "undefined" && localStorage.getItem(GRC_OV_LAYOUT_KEY) !== null,
  );
  /* Single mutation point for membership/order - keeps state + storage in sync. */
  const commitLayout = (ids: string[]) => { setLayout(ids); saveLayout(GRC_OV_LAYOUT_KEY, ids); };
  const toggleCard = (id: string) =>
    commitLayout(layout.includes(id) ? layout.filter((x) => x !== id) : [...layout, id]);
  const removeCard = (id: string) => commitLayout(layout.filter((x) => x !== id));
  const reorderCards = (ids: string[]) => {
    const extras = layout.filter((id) => !ids.includes(id));
    commitLayout([...ids, ...extras]);
  };
  const resetLayout = () => commitLayout([...defaultIdsRef.current]);
  const cards = layout.filter((id) => GRC_OV_CARD_MAP[id]);
  /* Catalog cards the user is allowed to add (drives More/Extra/Explore). */
  const availableCards = GRC_OV_CARDS.filter((c) => availableIds.includes(c.id));

  const updateWidgets = useCallback((response: MyWidgetsResponse, catalogIds: string[] = []) => {
    setWidgetsResponse(response);
    const roles = response.roles ?? [];
    const enabledSet = new Set<WidgetKey>();   // personal ON
    const universeSet = new Set<WidgetKey>();  // everything the admin chose for the role
    for (const role of roles) {
      for (const w of role.widgets ?? []) {
        universeSet.add(w.widget);
        if (w.enabled) enabledSet.add(w.widget);
      }
    }
    const enabled = Array.from(enabledSet);

    const isCard = (id: string) => !!GRC_OV_CARD_MAP[id];
    
    const universeCardIds = adminUniverseRef.current.length
      ? adminUniverseRef.current
      : enabledCardIds(Array.from(universeSet), ORG_WIDGET_TO_CARD, isCard).ids;
    const universeIds = universeCardIds.filter(isCard);
    const allDisabled = universeIds.length === 0;
    const { ids: enabledIds } = enabledCardIds(enabled, ORG_WIDGET_TO_CARD, isCard);

  
    
    const fullCatalogIds = catalogIds.filter(isCard);
    const finalEnabledIds = allDisabled ? [] : [...enabledIds, ...ORG_EXTRA_CARDS];
    const finalUniverseIds = allDisabled
      ? []
      : [...(fullCatalogIds.length ? fullCatalogIds : universeIds), ...ORG_EXTRA_CARDS];

    setWidgetsDisabled(allDisabled);
    setAvailableIds(finalUniverseIds);
    defaultIdsRef.current = finalEnabledIds;

    if (allDisabled) {
      commitLayout([]);
      hadSavedLayout.current = true;
    } else {
      setLayout((prevLayout) => {
        const keep = prevLayout.filter((id) => finalEnabledIds.includes(id));
        const added = finalEnabledIds.filter((id) => !keep.includes(id));
        const next = [...keep, ...added];
        saveLayout(GRC_OV_LAYOUT_KEY, next);
        return next;
      });
      hadSavedLayout.current = true;
    }
  }, []);

  /* ── Enabled widgets (drive both the panel list and the grid) ────────────
     available = the role's enabled widgets (+ extras), so the "Add cards" panel
     only offers what the Dashboard Config enabled; the layout shown directly is
     the same enabled set. When nothing is enabled → configure message. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        /* my-widgets = personal on/off; roles list lets us resolve each role's id
           so we can read the admin's full selection from the role-widgets API. */
        const [response, rolesRes, catalog] = await Promise.all([
          fetchMyWidgets(),
          getAPI(API_URL.PLATFORM_ROLES + "/all"),
          fetchWidgetCatalog().catch(() => [] as WidgetKey[]),  // full DC catalog → Manage universe
        ]);
        if (cancelled) return;
       
        const catalogCardIds = widgetsToCards(catalog, ORG_WIDGET_TO_CARD).filter(
          (id) => !!GRC_OV_CARD_MAP[id],
        );
        const rolesList: Array<{ id: string; name?: string; displayName?: string }> = rolesRes?.data ?? [];
        const norm = (s: string) => s.toLowerCase().replace(/[\s_-]/g, "");
        /* (orgId, roleId) for each of the user's roles → admin universe = the
           union of their role-widgets selections. */
        const pairs = (response.roles ?? [])
          .map((b) => ({
            orgId: b.orgUnitId,
            roleId: rolesList.find((r) => norm(r.displayName ?? r.name ?? "") === norm(b.roleName ?? ""))?.id,
          }))
          .filter((p): p is { orgId: string; roleId: string } => !!p.orgId && !!p.roleId);
        const lists = await Promise.all(
          pairs.map((p) => fetchRoleWidgets(p.orgId, p.roleId).catch(() => [])),
        );
        if (cancelled) return;
        const universe = new Set<WidgetKey>();
        lists.forEach((l) => l.forEach((w) => universe.add(w.widget)));
        adminUniverseRef.current = enabledCardIds(Array.from(universe), ORG_WIDGET_TO_CARD, (id) => !!GRC_OV_CARD_MAP[id]).ids;
        updateWidgets(response, catalogCardIds);
      } catch {
        if (cancelled) return;
        /* No config data → show nothing (not the whole catalog mock). */
        setWidgetsDisabled(true);
        setAvailableIds([]);
        defaultIdsRef.current = [];
        commitLayout([]);
        hadSavedLayout.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [updateWidgets]);

  /* ── Live JSX for every configurable card (keyed by card id) ─────────── */
  const kpiNode = (id: string) => {
    const k = kpiData.find((x) => x.id === id);
    return k ? <KpiCard kpi={k} index={0} loading={loading} /> : null;
  };
  const renderMap: Record<string, ReactNode> = {
    total_apps: kpiNode("total_apps"),
    active_ssps: kpiNode("active_ssps"),
    compliance_score: kpiNode("compliance_score"),
    open_findings: kpiNode("open_findings"),
    assessments_running: kpiNode("assessments_running"),
    assessment_pipeline: (
      <Card className="flex h-full flex-col">
        <SectionTitle title="Assessment Status Overview" subtitle="Active assessments by stage" />
        <div className="flex flex-1 flex-col justify-center">
          {loading ? <PipelineSkeleton /> : pipeline.some((s) => s.count > 0) ? <PipelineWidget stages={pipeline} /> : <NoData />}
        </div>
      </Card>
    ),
    compliance_framework: (
      <Card className="flex h-full flex-col">
        <SectionTitle title="Compliance by Framework" subtitle="Compliance coverage across control catalogs" />
        <div className="min-h-0 flex-1">
          {loading ? (
            <FrameworkSkeleton />
          ) : (
            <DonutPanel
              slices={frameworks.map((fw) => ({ label: fw.name, value: fw.pct, pct: fw.pct, color: fw.color }))}
              centerValue={activeSspCount}
              centerLabel="apps"
            />
          )}
        </div>
      </Card>
    ),
    top_risk_apps: (
      <Card className="flex h-full flex-col">
        <SectionTitle title="Top Risk Applications" subtitle="Applications with the most open findings" />
        <div className="flex-1">
          {!loading && riskApps.length === 0 ? (
            <NoData />
          ) : (
            <DataTable<RiskAppItem>
              columns={riskAppColumns}
              data={sortedRiskApps}
              loading={loading}
              rowKey={(r, i) => `${r.name}-${i}`}
              emptyText="No Data Available"
              maxHeight="256px"
              sortKey={riskSortKey}
              sortDir={riskSortDir}
              onSort={handleRiskSort}
            />
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate("/cmdb/applications")}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-foreground/[0.07] bg-foreground/[0.02] py-2 text-xs font-semibold text-muted-foreground transition-all hover:bg-foreground/[0.05] hover:text-foreground/70"
        >
          View All Applications
          <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </button>
      </Card>
    ),
    poam_status: (
      <Card className="flex h-full flex-col">
        <SectionTitle title="Findings & POA&M Status" subtitle="Open findings and remediation progress" />
        <div className="flex flex-1 flex-col justify-center">
          {loading ? <FrameworkSkeleton /> : poamStatus.length === 0 ? <NoData /> : <PoamChart items={poamStatus} />}
        </div>
      </Card>
    ),
  };

  if (!mounted) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="size-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      {/* Side panel (embedded) needs its own padding; as the main page the
          app layout already provides it — match the group view (no extra px). */}
      <motion.div
        className={`bg-background ${embeddedOrg ? "px-5 py-4 lg:px-6" : ""}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >

        {/* ══ PAGE HEADER ══════════════════════════════════════════════ */}
        <motion.div
          className={`mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${embeddedOrg ? "pr-10" : ""}`}
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {dashboardTitle}
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {dashboardSubtitle}
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Customize button hidden
            {!editing && (
              <button
                type="button"
                onClick={() => setCustomizeOpen(true)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground/80 transition-colors hover:border-indigo-400/40 hover:text-foreground"
              >
                <SlidersHorizontal className="size-3.5" />
                Customize
              </button>
            )}
            */}
            {/* Layout edit controls — hidden when no widgets are configured */}
            {!widgetsDisabled && (editing ? (
              <>
                <button
                  type="button"
                  onClick={resetLayout}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-transparent px-3 text-xs font-semibold text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
                >
                  <RotateCcw className="size-3.5" />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-indigo-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
                >
                  <Check className="size-3.5" />
                  Done
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setExtraOpen((o) => !o)}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors ${
                    extraOpen
                      ? "border-indigo-500/50 bg-indigo-500/[0.08] text-foreground"
                      : "border-border bg-card text-foreground/80 hover:border-indigo-400/40 hover:text-foreground"
                  }`}
                >
                  <LayoutGrid className="size-3.5" />
                  Manage Dashboards
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground/80 transition-colors hover:border-indigo-400/40 hover:text-foreground"
                >
                  <Pencil className="size-3.5" />
                  Edit layout
                </button>
              </>
            ))}
            <button
              type="button"
              onClick={fetchAll}
              disabled={loading}
              title="Refresh data"
              className="flex size-9 items-center justify-center rounded-lg bg-indigo-600 text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
            >
              <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
            </button>

            {/* Organisation dropdown — read-only chip when embedded in drill-down */}
            {embeddedOrg ? (
              <div className="flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3.5 text-sm font-medium text-foreground/70">
                <Building2 className="size-4 shrink-0 text-blue-400" />
                <span className="max-w-[220px] truncate">{embeddedOrg.name}</span>
              </div>
            ) : (
            <div className="relative">
              <button
                type="button"
                onClick={() => { setDraftOrgIds(appliedOrgIds); setOrgOpen((p) => !p); }}
                className="flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3.5 text-sm font-medium text-foreground/70 transition-colors hover:border-blue-500/40 hover:text-foreground"
              >
                <Building2 className="size-4 shrink-0 text-blue-400" />
                <span className="max-w-[180px] truncate">
                  {appliedOrgIds.length === 0
                    ? "All Organisations"
                    : `${appliedOrgIds.length} selected`}
                </span>
                <ChevronDown
                  className={`size-4 shrink-0 text-muted-foreground transition-transform duration-200 ${orgOpen ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence>
                {orgOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setOrgOpen(false)} />
                    <motion.div
                      className="absolute right-0 top-full z-20 mt-1.5 w-56 overflow-hidden rounded-xl border border-border bg-popover shadow-xl"
                      initial={{ opacity: 0, y: -6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.97 }}
                      transition={{ duration: 0.14 }}
                    >
                      <div className="border-b border-foreground/[0.05] px-3 py-2">
                        <span className="text-[10px] font-semibold tracking-wider text-muted-foreground">
                          Filter by Organisation
                        </span>
                      </div>
                      {orgsLoading ? (
                        <div className="flex items-center gap-2 px-3 py-3">
                          <div className="size-3 animate-spin rounded-full border border-blue-400 border-t-transparent" />
                          <span className="text-[11px] text-muted-foreground">Loading…</span>
                        </div>
                      ) : (
                        <>
                          <div className="max-h-56 overflow-y-auto py-1">
                            {orgList.map((org) => {
                              const isAll = org.id === "";
                              const checked = isAll
                                ? draftOrgIds.length === 0
                                : draftOrgIds.includes(org.id);
                              return (
                                <button
                                  key={org.id || "all"}
                                  type="button"
                                  onClick={() => toggleDraftOrg(org.id)}
                                  className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-xs transition-colors hover:bg-foreground/[0.04] ${
                                    checked ? "text-foreground" : "text-muted-foreground"
                                  }`}
                                >
                                  <span
                                    className={`flex size-4 shrink-0 items-center justify-center rounded border transition-colors ${
                                      checked
                                        ? "border-blue-500 bg-blue-500 text-white"
                                        : "border-border bg-transparent"
                                    }`}
                                  >
                                    {checked && <Check className="size-3" />}
                                  </span>
                                  <span className="truncate">{org.name}</span>
                                </button>
                              );
                            })}
                          </div>
                          {/* Apply (filter) button */}
                          <div className="border-t border-foreground/[0.05] p-2">
                            <button
                              type="button"
                              onClick={applyOrgFilter}
                              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-indigo-600 py-2 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
                            >
                              <Filter className="size-3.5" />
                              Apply Filter
                              {draftOrgIds.length > 0 && ` (${draftOrgIds.length})`}
                            </button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            )}
          </div>
        </motion.div>

        {/* ══ ERROR BANNER ═════════════════════════════════════════════ */}
        <AnimatePresence>
          {error && (
            <motion.div
              className="mb-3 flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.08] px-3 py-2.5 text-xs text-amber-400"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <AlertCircle className="size-3.5 shrink-0" />
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ══ EDIT-MODE BANNER ═════════════════════════════════════════ */}
        {editing && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-indigo-500/20 bg-indigo-500/[0.06] px-3 py-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">
            <Pencil className="size-3.5 shrink-0" />
            Editing layout - drag any card to move it; cards reflow to fill the space.
          </div>
        )}

        {/* ══ CONFIGURE MESSAGE / EXTRA-CARDS / CONFIGURABLE CARD GRID ═ */}
        {widgetsDisabled ? (
          <ConfigureWidgetsMessage />
        ) : extraOpen ? (
          <ExtraCardsPanel
            active={layout}
            cards={availableCards}
            renderMap={renderMap}
            sizeOf={(id) => GRC_OV_CARD_MAP[id]?.size}
            onApply={(ids) => commitLayout(ids)}
            onClose={() => { setExtraOpen(false); setPanelCardId(undefined); }}
            highlightId={panelCardId}
            onClearHighlight={() => setPanelCardId(undefined)}
          />
        ) : cards.length === 0 ? (
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border">
            <p className="text-sm font-semibold text-foreground">Shape Your Perfect Dashboard</p>
            <p className="max-w-xs text-center text-xs text-muted-foreground">
              Add the cards and charts that matter most to you and build a workspace that works the way you do.
            </p>
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
            >
              <Plus className="size-3.5" />
              Add cards
            </button>
          </div>
        ) : (
          <ConfigurableCardGrid
            items={cards}
            renderMap={renderMap}
            editing={editing}
            onRemove={removeCard}
            onReorder={reorderCards}
            sizeOf={(id) => GRC_OV_CARD_MAP[id]?.size}
          />
        )}

        {/* ══ EXPLORE CARDS CAROUSEL ═══════════════════════════════════ */}
        {!widgetsDisabled && !extraOpen && (
          <div className="mt-4">
            <CardLibraryCarousel
              title="Explore cards"
              subtitle="Cards you haven't added yet. View details to add one to your dashboard."
              cards={availableCards.filter((c) => !layout.includes(c.id))}
              onViewDetail={(id) => {
                /* Open the Manage-dashboard panel with that card spotlighted. */
                setPanelOpen(false);
                setPanelCardId(id);
                setExtraOpen(true);
              }}
            />
          </div>
        )}

      </motion.div>

      {/* ══ ADD-CARDS SIDE PANEL ═════════════════════════════════════ */}
      <AddCardsPanel
        open={panelOpen}
        active={layout}
        cards={availableCards}
        categories={GRC_OV_CATEGORIES}
        onToggle={toggleCard}
        onClose={() => { setPanelOpen(false); setPanelCardId(undefined); }}
        onEdit={() => {
          setPanelOpen(false);
          setPanelCardId(undefined);
          setEditing(true);
        }}
        renderPreview={(id) => renderMap[id]}
        initialExpandedId={panelCardId}
      />
      <CustomizeWidgetsPanel
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        widgetsResponse={widgetsResponse}
        onWidgetsChanged={updateWidgets}
      />
    </>
  );
}

/* ══ SKELETON LOADERS ═══════════════════════════════════════════════════ */
function SkeletonBlock({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-foreground/[0.06] ${className}`} />;
}
function PipelineSkeleton() {
  return (
    <div className="flex gap-0">
      {[1, 2, 3, 4, 5].map((i) => <SkeletonBlock key={i} className="h-24 flex-1 mx-0.5" />)}
    </div>
  );
}
function FrameworkSkeleton() {
  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col gap-3 flex-1">
        {[1,2,3,4,5].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="size-2 rounded-full animate-pulse bg-foreground/[0.06]" />
            <div className="h-2.5 w-20 animate-pulse rounded bg-foreground/[0.06]" />
            <div className="h-[7px] flex-1 animate-pulse rounded-full bg-foreground/[0.06]" />
            <div className="h-2.5 w-6 animate-pulse rounded bg-foreground/[0.06]" />
          </div>
        ))}
      </div>
      <div className="size-[220px] rounded-full animate-pulse bg-foreground/[0.06] shrink-0" />
    </div>
  );
}
